import { useEffect, useRef } from 'react';

// Global audio instance to persist across components
let globalAudio: HTMLAudioElement | null = null;
let isPlaying = false;

/**
 * Hook for managing background music
 * Music persists across component mounts/unmounts
 * Stops when explicitly called or on cleanup
 */
export function useBackgroundMusic(src: string, options?: {
  volume?: number;
  loop?: boolean;
  autoPlay?: boolean;
}) {
  const { volume = 0.5, loop = true, autoPlay = true } = options || {};
  const hasStartedRef = useRef(false);

  useEffect(() => {
    // Create audio only once
    if (!globalAudio) {
      globalAudio = new Audio(src);
      globalAudio.loop = loop;
      globalAudio.volume = volume;
      console.log('🎵 Background music initialized:', src);
    }

    // Update settings
    globalAudio.volume = volume;
    globalAudio.loop = loop;

    // Auto-play if enabled and not already playing
    if (autoPlay && !isPlaying && !hasStartedRef.current) {
      hasStartedRef.current = true;

      // Try to play (may be blocked by browser autoplay policy)
      const playPromise = globalAudio.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            isPlaying = true;
            console.log('🎵 Background music playing');
          })
          .catch((err) => {
            console.log('🎵 Autoplay blocked, waiting for user interaction:', err.message);

            // Add one-time click handler to start music
            const startOnInteraction = () => {
              if (globalAudio && !isPlaying) {
                globalAudio.play()
                  .then(() => {
                    isPlaying = true;
                    console.log('🎵 Background music started after interaction');
                  })
                  .catch(() => {});
              }
              document.removeEventListener('click', startOnInteraction);
              document.removeEventListener('touchstart', startOnInteraction);
            };

            document.addEventListener('click', startOnInteraction, { once: true });
            document.addEventListener('touchstart', startOnInteraction, { once: true });
          });
      }
    }

    // No cleanup - music persists
  }, [src, volume, loop, autoPlay]);

  return {
    play: () => {
      if (globalAudio && !isPlaying) {
        globalAudio.play()
          .then(() => {
            isPlaying = true;
            console.log('🎵 Music playing');
          })
          .catch(() => {});
      }
    },
    pause: () => {
      if (globalAudio && isPlaying) {
        globalAudio.pause();
        isPlaying = false;
        console.log('🎵 Music paused');
      }
    },
    stop: () => {
      if (globalAudio) {
        globalAudio.pause();
        globalAudio.currentTime = 0;
        isPlaying = false;
        console.log('🎵 Music stopped');
      }
    },
    setVolume: (vol: number) => {
      if (globalAudio) {
        globalAudio.volume = Math.max(0, Math.min(1, vol));
      }
    },
    isPlaying: () => isPlaying,
  };
}

/**
 * Stop and cleanup background music completely
 * Call this when leaving the login flow
 */
export function stopBackgroundMusic() {
  if (globalAudio) {
    globalAudio.pause();
    globalAudio.currentTime = 0;
    globalAudio = null;
    isPlaying = false;
    console.log('🎵 Background music stopped and cleaned up');
  }
}
